import React from 'react'
import { IDriverManifestsTrip } from 'src/types/driver-manifests'
import UpdatedTooltip from '../ui/UpdatedTooltip'
import {
  cancelledTripsStatusesDashboard,
  completedTripStatusesDashboard,
  TripStatuses
} from 'src/constants/trip-statuses'
import moment from 'moment'
import Warning from '../icons/auth/Warning'

export interface IDriverManifestsTripDetailsPaneProps {
  selectedTrip: IDriverManifestsTrip
}

const DriverManifestsTripDetailsPane = (props: IDriverManifestsTripDetailsPaneProps) => {
  const { selectedTrip } = props

  const truncateText = (text: string): string => {
    return text.slice(0, 8) + '...'
  }

  const getStatusText = () => {
    if (selectedTrip.hasIssue) {
      if (selectedTrip.notificationMessage.toLowerCase().includes('vehicle')) {
        return 'Vehicle Issue'
      } else if (selectedTrip.notificationMessage.toLowerCase().includes('behind')) {
        return 'Late'
      } else if (selectedTrip.lastStatus === TripStatuses.NO_SHOW_RIDER) {
        return 'No Show'
      } else if (cancelledTripsStatusesDashboard.includes(selectedTrip.lastStatus)) {
        return 'Cancelled'
      } else {
        return 'Has Issue'
      }
    } else if (cancelledTripsStatusesDashboard.includes(selectedTrip.lastStatus)) {
      return 'Cancelled'
    } else if (completedTripStatusesDashboard.includes(selectedTrip.lastStatus)) {
      return 'Completed'
    } else {
      return 'On Time'
    }
  }

  const getStatusColor = () => {
    if (selectedTrip.hasIssue) {
      if (
        selectedTrip.notificationMessage.toLowerCase().includes('vehicle') ||
        selectedTrip.lastStatus === TripStatuses.NO_SHOW_RIDER
      ) {
        return 'dm-tag-red'
      } else if (selectedTrip.notificationMessage.toLowerCase().includes('behind')) {
        return 'dm-tag-orange'
      } else if (cancelledTripsStatusesDashboard.includes(selectedTrip.lastStatus)) {
        return 'dm-tag-gray'
      } else {
        return 'dm-tag-red'
      }
    } else if (cancelledTripsStatusesDashboard.includes(selectedTrip.lastStatus)) {
      return 'dm-tag-gray'
    } else {
      return 'dm-tag-green'
    }
  }

  const checkNote = () => {
    if (selectedTrip.notes) {
      if (selectedTrip.notes.length === 1 && selectedTrip.notes === ';') {
        return false
      } else {
        return true
      }
    } else {
      return false
    }
  }

  const getLastStatusTime = () => {
    const lastStatus = selectedTrip.statuses[0]
    const statusTime = moment(lastStatus.createdAt).format('hh:mm A')
    return `${statusTime}`
  }

  const countryList = new Set(['Philippines', 'United States', 'USA'])

  const removeCountrySuffix = (input: string): string => {
    const parts = input.split(',').map(part => part.trim())
    const lastPart = parts[parts.length - 1]

    if (parts.length > 1 && countryList.has(lastPart)) {
      return parts.slice(0, -1).join(', ')
    }

    return input
  }

  const getTripDetailsWarningMessage = () => {
    if (selectedTrip.notificationMessage.toLowerCase().includes('vehicle')) {
      if (selectedTrip.notificationMessage.toLowerCase().includes('mechanical')) {
        return 'Driver reported vehicle malfunction'
      } else if (selectedTrip.notificationMessage.toLowerCase().includes('capacity')) {
        return 'Vehicle capacity issue has been reported'
      } else {
        return 'Vehicle accomodation capability issue'
      }
    } else if (selectedTrip.notificationMessage.toLowerCase().includes('behind')) {
      return 'This trip is behind schedule'
    } else if (selectedTrip.lastStatus === TripStatuses.NO_SHOW_RIDER) {
      return 'Passenger did not show up at pickup location'
    } else if (cancelledTripsStatusesDashboard.includes(selectedTrip.lastStatus)) {
      return 'This trip has been cancelled'
    } else {
      return 'This trip has issues'
    }
  }

  return (
    <div className='dm-trip-details-pane'>
      <div className='dm-trip-details-header'>
        <div>
          <div className='dm-driver-header'>
            {selectedTrip.rider.firstName} {selectedTrip.rider.lastName}
          </div>
          <div className='dm-trip-id-section'>
            <UpdatedTooltip text={'Trip ID: ' + selectedTrip.id}>
              Trip ID: {truncateText(selectedTrip.id)}
            </UpdatedTooltip>
          </div>
        </div>
        <div className='flex items-center'>
          <div className={getStatusColor()}>{getStatusText()}</div>
        </div>
      </div>
      {getStatusColor() !== 'dm-tag-green' && (
        <div className='dm-trip-details-section dm-warning'>
          <div className='dm-warning-indicator'>
            <Warning className='svg-yellow' />
            <div className='dm-warning-header'>{getTripDetailsWarningMessage()}</div>
          </div>
          <div className='dm-warning-subtitle'>Updated at {getLastStatusTime()}</div>
        </div>
      )}
      <div className='dm-trip-details-section'>
        <div className='dm-driver-header'>Schedule Information</div>
        <div className='dm-trip-details-schedule-info'>
          <div className='flex flex-column w50p'>
            <div className='dm-driver-subtitle'>Pickup Time</div>
            <div className='flex flex-row pt1'>
              <img className='dm-icon' src='/images/clock.png' alt='Clock icon' />
              <div className='dm-schedule-time'>
                {' '}
                {moment(selectedTrip.scheduledPickupTime).isValid()
                  ? moment(selectedTrip.scheduledPickupTime).format('hh:mm A')
                  : 'Not Available'}
              </div>
            </div>
          </div>
          <div className='flex flex-column w50p'>
            <div className='dm-driver-subtitle'>Dropoff Time</div>
            <div className='flex flex-row pt1'>
              <img className='dm-icon' src='/images/clock.png' alt='Clock icon' />
              <div className='dm-schedule-time'>
                {' '}
                {moment(selectedTrip.scheduledDropoffTime).isValid()
                  ? moment(selectedTrip.scheduledDropoffTime).format('hh:mm A')
                  : 'Not Available'}
              </div>
            </div>
          </div>
        </div>
        <div className='dm-trip-details-schedule-info'>
          <div className='flex flex-column w50p'>
            <div className='dm-driver-subtitle'>Estimated Pickup Time</div>
            <div className='flex flex-row pt1'>
              <img className='dm-icon' src='/images/clock.png' alt='Clock icon' />
              <div className='dm-schedule-time'>
                {selectedTrip.etaPickup ? moment(selectedTrip.etaPickup).format('hh:mm A') : 'Not Available'}
              </div>
            </div>
          </div>
          <div className='flex flex-column w50p'>
            <div className='dm-driver-subtitle'>Estimated Dropoff Time</div>
            <div className='flex flex-row pt1'>
              <img className='dm-icon' src='/images/clock.png' alt='Clock icon' />
              <div className='dm-schedule-time'>
                {selectedTrip.etaDropoff ? moment(selectedTrip.etaDropoff).format('hh:mm A') : 'Not Available'}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='dm-trip-details-section'>
        <div className='dm-driver-header'>Location Information</div>
        <div className='dm-trip-details-location-info'>
          <div className='flex flex-column'>
            <div className='dm-driver-subtitle'>Pickup Location</div>
            <div className='flex flex-row pt1 items-center'>
              <img className='dm-icon' src='/images/map-pin.png' alt='Map Pin Icon' />
              <div className='dm-schedule-time'>
                {selectedTrip.pickupLocation.fullAddress
                  ? removeCountrySuffix(selectedTrip.pickupLocation.fullAddress)
                  : 'Not Available'}
              </div>
            </div>
          </div>
          <div className='flex flex-column pt2'>
            <div className='dm-driver-subtitle'>Dropoff Location</div>
            <div className='flex flex-row pt1 items-center'>
              <img className='dm-icon' src='/images/map-pin.png' alt='Map Pin Icon' />
              <div className='dm-schedule-time'>
                {selectedTrip.dropoffLocation.fullAddress
                  ? removeCountrySuffix(selectedTrip.dropoffLocation.fullAddress)
                  : 'Not Available'}
              </div>
            </div>
          </div>
        </div>
      </div>
      {checkNote() && (
        <div className='dm-trip-details-section'>
          <div className='dm-driver-header'>Notes</div>
          <div className='dm-notes'>{selectedTrip.notes}</div>
        </div>
      )}
      {selectedTrip.notificationMessage && (
        <div className='dm-trip-details-section'>
          <div className='dm-driver-header'>Notification Message</div>
          <div className='dm-notes'>{selectedTrip.notificationMessage}</div>
        </div>
      )}
    </div>
  )
}

export default DriverManifestsTripDetailsPane



// WEBPACK FOOTER //
// ./src/components/driver-manifests/DriverManifestsTripDetailsPane.tsx